import { Icon } from "@iconify/react";
import { PlusCircleIcon, XCircleIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Loader } from "@/components/motion/loader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useI18n } from "@/locales/i18n-provider";

export type DataTableFilterOption = {
  label: string;
  value: string;
  icon?: string;
};

type DataTableFacetedFilterProps = {
  label: string;
  icon?: string;
  /** 选项正在加载；面板内显示加载态，触发器保持可用。 */
  loading?: boolean;
  multiple?: boolean;
  options: ReadonlyArray<DataTableFilterOption>;
  /** 已有结果、正在按新关键词重查；结果留在原位，转圈只出现在搜索框里。 */
  searching?: boolean;
  /** 关键词。与 `onSearchChange` 一起传入即切到服务端搜索，cmdk 不再本地过滤。 */
  searchValue?: string;
  /** 已选项的展示信息。选项按需加载时选中项未必在当前结果里，触发器徽标靠它渲染。 */
  selectedOptions?: ReadonlyArray<DataTableFilterOption>;
  values: ReadonlyArray<string>;
  onOpenChange?: (open: boolean) => void;
  onSearchChange?: (value: string) => void;
  onValuesChange: (values: string[]) => void;
};

/** 选项按需加载的筛选器要向调用方传的那组 props，由对应的 options Hook 一次性返回。 */
export type DataTableAsyncFilterProps = Required<
  Pick<DataTableFacetedFilterProps, "loading" | "options" | "searching" | "searchValue" | "selectedOptions" | "onOpenChange" | "onSearchChange">
>;

/** Dice UI 的虚线字段筛选器，改成受控状态以复用项目现有的 React Router 查询参数。 */
export function DataTableFacetedFilter({
  label,
  loading = false,
  multiple = true,
  options,
  searching = false,
  searchValue,
  selectedOptions,
  values,
  onOpenChange,
  onSearchChange,
  onValuesChange,
}: DataTableFacetedFilterProps) {
  const { intlLocale, t } = useI18n();
  const numberFormatter = new Intl.NumberFormat(intlLocale);
  const [open, setOpen] = useState(false);
  const selectedValues = useMemo(() => new Set(values), [values]);
  /** 服务端已按关键词收窄，cmdk 再过一遍只会把结果藏掉。 */
  const searchesOnServer = Boolean(onSearchChange);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const toggleOption = (value: string) => {
    if (!multiple) {
      onValuesChange(selectedValues.has(value) ? [] : [value]);
      changeOpen(false);
      return;
    }

    const next = new Set(selectedValues);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onValuesChange([...next]);
  };

  const badgeOptions = selectedOptions ?? options.filter((option) => selectedValues.has(option.value));
  /** 选中项标签还没回来时先退回计数徽标，别让触发器只剩一根分隔线。 */
  const showCountBadge = selectedValues.size > 2 || badgeOptions.length === 0;

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <Button size="lg" className="border-dashed border-input font-normal" variant="outline">
          {selectedValues.size > 0 ? (
            <XCircleIcon
              aria-label={t("dataTable.filter.clearLabel", { label })}
              className="pointer-events-auto! cursor-pointer"
              data-icon="inline-start"
              onClick={(event) => {
                event.stopPropagation();
                changeOpen(false);
                onValuesChange([]);
              }}
            />
          ) : (
            <PlusCircleIcon data-icon="inline-start" />
          )}
          {label}
          {selectedValues.size > 0 ? (
            <>
              <Separator className="mx-0.5 my-auto data-[orientation=vertical]:h-5" orientation="vertical" />
              <Badge className="rounded-sm px-1 font-normal lg:hidden" variant="secondary">
                {numberFormatter.format(selectedValues.size)}
              </Badge>
              <span className="hidden items-center gap-1 lg:flex">
                {showCountBadge ? (
                  <Badge className="rounded-sm px-1 font-normal" variant="secondary">
                    {t("dataTable.filter.selected", { count: numberFormatter.format(selectedValues.size) })}
                  </Badge>
                ) : (
                  badgeOptions.map((option) => (
                    <Badge className="rounded-sm px-1 font-normal" key={option.value} variant="secondary">
                      {option.icon && <Icon className="size-3 shrink-0" icon={option.icon} />}
                      {option.label}
                    </Badge>
                  ))
                )}
              </span>
            </>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <Command shouldFilter={!searchesOnServer}>
          {/* 重查时结果靠 keepPreviousData 留在原位，反馈只放在搜索框右侧，列表不闪。 */}
          <div className="relative data-searching:[&_[data-slot=command-input-wrapper]_svg]:invisible" data-searching={searching || undefined}>
            <CommandInput placeholder={t("dataTable.filter.search", { label })} value={searchValue} onValueChange={onSearchChange} />
            {searching ? (
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                <Loader className="text-muted-foreground" label={t("dataTable.filter.searching", { label })} size={14} />
              </span>
            ) : null}
          </div>
          <CommandList>
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader className="text-muted-foreground" label={t("dataTable.filter.loading", { label })} size={16} />
              </div>
            ) : (
              <CommandEmpty>{t("dataTable.filter.noMatches")}</CommandEmpty>
            )}
            <CommandGroup>
              {options.map((option) => {
                const selected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    data-checked={selected}
                    key={option.value}
                    keywords={[option.label]}
                    value={option.value}
                    onSelect={() => toggleOption(option.value)}
                  >
                    {option.icon && <Icon className="size-3 shrink-0" icon={option.icon} />}
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {/* 单选面板不放这一行：选中即关闭，它只会在淡出途中把面板顶高一截，而取消选择点已选项或触发器上的 X 都行。 */}
            {multiple && selectedValues.size > 0 ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem className="justify-center" onSelect={() => onValuesChange([])}>
                    {t("dataTable.filter.clear")}
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
